import "./AssociationsDetails.css";

import { Column, Divider, ReadonlyText, Row } from "../../../../../../../widget";
import { CollaboratorDetails, LandholdingDetails, ProjectDetails } from "../../useDealStructure";

interface AssociationsDetailsProps {
  selectedCompany: CollaboratorDetails | undefined;
  selectedProjects: ProjectDetails[];
  selectedLandholdings: LandholdingDetails[];
}

export const AssociationsDetails = ({
  selectedCompany,
  selectedProjects,
  selectedLandholdings,
}: AssociationsDetailsProps): JSX.Element => {
  const hasAssociations = Boolean(selectedCompany) || selectedProjects.length > 0 || selectedLandholdings.length > 0;

  return (
    <div className="DealStructureCard DealStructureAssociationsCard">
      <div className="AssociationDetailsCardHeader">
        <h2>Association details</h2>
      </div>
      {!hasAssociations ? (
        <p className="body2 DealStructurePanelEmpty">Select associations on an investment to view details here.</p>
      ) : null}
      {selectedCompany && (
        <div className="DealStructureAssociationSection">
          <Row spacingV="l">
            <Column span={12}>
              <p className="body01">Company</p>
            </Column>
          </Row>
          <div className="DealStructureAssociationsGrid">
            <ReadonlyText label="Name" value={selectedCompany.name} />
            <ReadonlyText label="Company type" value={selectedCompany.collaboratorType} />
            <ReadonlyText label="Description" value={selectedCompany.description} />
            <ReadonlyText label="Email address" value={selectedCompany.email} />
            <ReadonlyText label="Phone number" value={selectedCompany.phone} />
            <ReadonlyText label="Website" value={selectedCompany.website} />
            <ReadonlyText label="Address line 1" value={selectedCompany.addressLine1} />
            <ReadonlyText label="Address line 2" value={selectedCompany.addressLine2} />
            <ReadonlyText label="City" value={selectedCompany.addressCity} />
            <ReadonlyText label="County / Region / State" value={selectedCompany.addressRegion} />
            <ReadonlyText label="Postcode" value={selectedCompany.addressPostcode} />
            <ReadonlyText label="Country code" value={selectedCompany.addressCountryCode} />
            <ReadonlyText label="CRM ID" value={selectedCompany.externalReference} />
          </div>
          {(selectedProjects.length > 0 || selectedLandholdings.length > 0) && (
            <>
              <Row spacingV="ll" />
              <Row spacingV="ll">
                <Column span={12}>
                  <Divider type="horizontal" />
                </Column>
              </Row>
            </>
          )}
        </div>
      )}
      {selectedProjects.length > 0 && (
        <div className="DealStructureAssociationSection">
          <Row spacingV="l">
            <Column span={12}>
              <p className="body01">Projects</p>
            </Column>
          </Row>
          {selectedProjects.map((project, index) => (
            <div key={project.displayName}>
              <div className="DealStructureAssociationsGrid">
                <ReadonlyText label="Project name" value={project.displayName} />
                <ReadonlyText label="Standard" value={project.standard?.displayName} />
                <ReadonlyText label="Phase" value={project.phase} />
                <ReadonlyText label="Project type" value={project.type?.displayName} />
                <ReadonlyText label="Region" value={project.region} />
                <ReadonlyText label="Developer" value={selectedCompany?.name ?? project.developer?.displayName} />
                <ReadonlyText label="Location coordinates" value={project.locationCoordinates} />
                <ReadonlyText
                  label="Developer phone"
                  value={selectedCompany?.phone ?? project.developer?.phoneNumber}
                />
                <ReadonlyText label="Developer email" value={selectedCompany?.email ?? project.developer?.email} />
              </div>
              {(selectedProjects.length - 1 > index || selectedLandholdings.length > 0) && (
                <>
                  <Row spacingV="ll" />
                  <Row spacingV="ll">
                    <Column span={12}>
                      <Divider type="horizontal" />
                    </Column>
                  </Row>
                </>
              )}
            </div>
          ))}
        </div>
      )}
      {selectedLandholdings.length > 0 && (
        <div className="DealStructureAssociationSection">
          <Row spacingV="l">
            <Column span={12}>
              <p className="body01">Landholdings</p>
            </Column>
          </Row>
          {selectedLandholdings.map((landholding, index) => {
            return (
              <div key={landholding?.displayName}>
                <div className="DealStructureAssociationsGrid">
                  <ReadonlyText label="Landholding name" value={landholding?.displayName} />
                  <ReadonlyText label="Landholding reference" value={undefined} />
                  <ReadonlyText label="Landholding reference ID" value={undefined} />
                  <ReadonlyText label="Region / Country / State" value={landholding?.location?.addressRegion} />
                  <ReadonlyText label="Location coordinates" value={landholding?.location?.locationCoordinates} />
                  <ReadonlyText label="Country" value={landholding?.location?.addressCountry} />
                </div>
                {selectedLandholdings.length - 1 > index && (
                  <>
                    <Row spacingV="ll" />
                    <Row spacingV="ll">
                      <Column span={12}>
                        <Divider type="horizontal" />
                      </Column>
                    </Row>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
